import React, { useEffect } from 'react';
import { Bell } from 'lucide-react';
import { useNotificationBell } from '@/stores/notificationCenterStore';

export interface NotificationBellProps {
  className?: string;
}

/**
 * NotificationBell Component v2 - FacturXPro Design System
 *
 * Cloche de notification avec badge de compteur
 * - Badge rouge si notifications urgentes
 * - Badge teal pour notifications normales
 * - Animation pulse si urgent
 *
 * @example
 * ```tsx
 * <NotificationBell />
 * ```
 *
 * Référence: DESIGN_SYSTEM.md section "Notification Bell"
 */
export const NotificationBell: React.FC<NotificationBellProps> = ({ className = '' }) => {
  const { unreadCount, hasUrgent, toggle, fetchUnreadCount } = useNotificationBell();

  // Fetch unread count on mount
  useEffect(() => {
    fetchUnreadCount();

    // Polling every 30 seconds (will be replaced by SSE later)
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, [fetchUnreadCount]);

  return (
    <button
      onClick={toggle}
      className={`
        relative bg-white/60 backdrop-blur-xl rounded-full p-3
        shadow-lg border border-white/60
        hover:bg-white/80 hover:shadow-xl
        transition-all duration-300
        group
        ${className}
      `}
      aria-label={`Notifications${unreadCount > 0 ? ` (${unreadCount} non lues)` : ''}`}
    >
      {/* Bell Icon */}
      <Bell
        className={`
          w-5 h-5 text-gray-700
          group-hover:text-teal-600
          transition-colors duration-300
          ${hasUrgent ? 'animate-[wiggle_0.5s_ease-in-out_infinite]' : ''}
        `}
      />

      {/* Badge */}
      {unreadCount > 0 && (
        <span
          className={`
            absolute -top-1 -right-1
            min-w-[20px] h-5 px-1.5
            flex items-center justify-center
            text-xs font-bold text-white
            rounded-full
            shadow-md
            ${hasUrgent
              ? 'bg-red-500 animate-pulse'
              : 'bg-teal-500'
            }
          `}
        >
          {unreadCount > 99 ? '99+' : unreadCount}
        </span>
      )}

      {/* Urgent Ping Animation */}
      {hasUrgent && (
        <span className="absolute -top-1 -right-1 w-5 h-5">
          <span className="absolute inset-0 rounded-full bg-red-400 animate-ping opacity-75" />
        </span>
      )}
    </button>
  );
};

export default NotificationBell;
